import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Message,
  DnsResultData,
  LogResultData,
  PingResultData,
  PortScanResultData,
  ToolResult,
  ToolStatus
} from './types';

const TOOL_ICONS: Record<string, string> = {
  ping: '⬡',
  traceroute: '⟿',
  dns_lookup: '⊛',
  port_scan: '⊡',
  get_interface_info: '⊞',
  get_routing_table: '⊟',
  check_connectivity: '◎',
  analyze_log_snippet: '≡',
};

const STATUS_COLORS: Record<ToolStatus, string> = {
  running: 'var(--accent-amber)',
  done: 'var(--accent-green)',
  error: 'var(--accent-red)',
};

interface KvProps {
  label: string;
  value: string | number | boolean;
  valueColor?: string;
}

const Kv = ({ label, value, valueColor }: KvProps): React.ReactElement => (
  <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
    <span style={{ width: '80px', color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
    <span style={{ color: valueColor ?? 'var(--text-primary)' }}>{String(value)}</span>
  </div>
);

interface InlineTextProps {
  text: string;
}

const InlineText = ({ text }: InlineTextProps): React.ReactElement => {
  const parts: React.ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('`')) {
      parts.push(
        <code
          key={match.index}
          style={{
            fontFamily: 'var(--font-mono)',
            background: 'var(--bg-void)',
            color: 'var(--text-code)',
            padding: '1px 5px',
            borderRadius: '3px',
            fontSize: '0.9em',
          }}
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('**')) {
      parts.push(
        <strong key={match.index} style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
          {token.slice(2, -2)}
        </strong>
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

interface MessageContentProps {
  text: string;
}

const MessageContent = ({ text }: MessageContentProps): React.ReactElement => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      elements.push(<div key={i} style={{ height: '0.5em' }} />);
      i++;
      continue;
    }

    if (/^\*\*[🔍🎯🛠️📋]/.test(line) || line.startsWith('## ') || line.startsWith('### ')) {
      const raw = line.replace(/^#{1,3}\s+/, '').replace(/\*\*/g, '');
      elements.push(
        <div
          key={i}
          style={{
            fontSize: '12px',
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--accent-cyan)',
            marginTop: '1.2em',
            marginBottom: '0.4em',
            paddingBottom: '4px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {raw}
        </div>
      );
      i++;
      continue;
    }

    if (line.startsWith('- ') || line.startsWith('* ') || /^\d+\.\s/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (
        i < lines.length &&
        (lines[i].startsWith('- ') || lines[i].startsWith('* ') || /^\d+\.\s/.test(lines[i]))
      ) {
        const content = lines[i].replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
        listItems.push(
          <li key={i} style={{ display: 'flex', gap: '6px', marginBottom: '2px' }}>
            <span style={{ color: 'var(--accent-cyan)', flexShrink: 0 }}>›</span>
            <span>
              <InlineText text={content} />
            </span>
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} style={{ listStyle: 'none', paddingLeft: '1em', marginBottom: '0.4em' }}>
          {listItems}
        </ul>
      );
      continue;
    }

    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      elements.push(
        <pre
          key={`code-${i}`}
          style={{
            background: 'var(--bg-void)',
            border: '1px solid var(--border)',
            borderLeft: '3px solid var(--accent-cyan-dim)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 12px',
            margin: '8px 0',
            fontSize: '12px',
            overflowX: 'auto',
            color: 'var(--text-code)',
            lineHeight: 1.5,
          }}
        >
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    elements.push(
      <div key={i} style={{ marginBottom: '2px' }}>
        <InlineText text={line} />
      </div>
    );
    i++;
  }

  return <div>{elements}</div>;
};

const PingResult = ({ r }: { r: PingResultData }): React.ReactElement => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <Kv label="host" value={r.host} />
    <Kv
      label="reachable"
      value={r.reachable ? '✓ yes' : '✗ no'}
      valueColor={r.reachable ? 'var(--accent-green)' : 'var(--accent-red)'}
    />
    {r.rtt_avg_ms != null && <Kv label="rtt avg" value={`${r.rtt_avg_ms} ms`} />}
    {r.packet_loss_pct != null && (
      <Kv
        label="loss"
        value={`${r.packet_loss_pct}%`}
        valueColor={r.packet_loss_pct > 0 ? 'var(--accent-amber)' : 'var(--accent-green)'}
      />
    )}
  </div>
);

const PortScanResult = ({ r }: { r: PortScanResultData }): React.ReactElement => (
  <div>
    {(r.port_scan ?? []).map((p) => (
      <div key={p.port} style={{ display: 'flex', gap: '12px', marginBottom: '2px', fontSize: '11px' }}>
        <span style={{ width: '50px', color: 'var(--text-secondary)' }}>{p.port}</span>
        <span
          style={{
            color:
              p.state === 'open'
                ? 'var(--accent-green)'
                : p.state === 'filtered'
                ? 'var(--accent-amber)'
                : 'var(--text-muted)',
            fontWeight: p.state === 'open' ? 600 : 400,
          }}
        >
          {p.state}
        </span>
      </div>
    ))}
  </div>
);

const DnsResult = ({ r }: { r: DnsResultData }): React.ReactElement => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <Kv label="hostname" value={r.hostname} />
    <Kv label="type" value={r.record_type} />
    <Kv
      label="resolved"
      value={r.resolved ? '✓ yes' : '✗ no'}
      valueColor={r.resolved ? 'var(--accent-green)' : 'var(--accent-red)'}
    />
    {r.records && r.records.length > 0 && (
      <div>
        <span style={{ color: 'var(--text-muted)', marginRight: '8px' }}>records</span>
        {r.records.map((rec, idx) => (
          <div key={idx} style={{ paddingLeft: '60px', color: 'var(--text-code)', fontSize: '11px' }}>
            {rec}
          </div>
        ))}
      </div>
    )}
  </div>
);

const LogResult = ({ r }: { r: LogResultData }): React.ReactElement => (
  <div>
    <Kv label="lines" value={r.lines_analyzed} />
    <Kv label="issues" value={r.has_issues ? `✓ ${r.findings?.length ?? 0} found` : '✗ none'} />
    {r.findings?.map((f, idx) => (
      <div key={idx} style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
        <span style={{ color: 'var(--accent-amber)' }}>▸</span>
        <span style={{ color: 'var(--text-code)' }}>{f.type}</span>
        <span style={{ color: 'var(--text-secondary)' }}>×{f.occurrences}</span>
      </div>
    ))}
  </div>
);

interface ResultDisplayProps {
  name: string;
  result: ToolResult;
}

const ResultDisplay = ({ name, result }: ResultDisplayProps): React.ReactElement => {
  if (name === 'ping') return <PingResult r={result as PingResultData} />;
  if (name === 'port_scan') return <PortScanResult r={result as PortScanResultData} />;
  if (name === 'dns_lookup') return <DnsResult r={result as DnsResultData} />;
  if (name === 'analyze_log_snippet') return <LogResult r={result as LogResultData} />;
  return (
    <pre style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {JSON.stringify(result, null, 2)}
    </pre>
  );
};

interface ToolCallCardProps {
  name: string;
  input: Record<string, unknown>;
  result: ToolResult | null;
  status: ToolStatus;
}

const ToolCallCard = ({ name, input, result, status }: ToolCallCardProps): React.ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const icon = TOOL_ICONS[name] ?? '○';
  const color = STATUS_COLORS[status] ?? 'var(--text-muted)';

  return (
    <div
      style={{
        border: `1px solid var(--border)`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface)',
        margin: '4px 0',
        overflow: 'hidden',
        animation: 'fadeIn 0.2s ease',
        fontSize: '12px',
      }}
    >
      <button
        onClick={() => result && setExpanded((e) => !e)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          padding: '8px 12px',
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: result ? 'pointer' : 'default',
          textAlign: 'left',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span style={{ color, fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>
          {status === 'running' ? <span style={{ fontSize: '13px' }}>◌</span> : icon}
        </span>
        <span style={{ color: 'var(--text-code)', fontWeight: 600 }}>{name}</span>
        <span
          style={{
            color: 'var(--text-secondary)',
            fontSize: '11px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {formatInput(input)}
        </span>
        {result && (
          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </span>
        )}
      </button>
      {expanded && result && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '10px 12px',
            background: 'var(--bg-void)',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          <ResultDisplay name={name} result={result} />
        </div>
      )}
    </div>
  );
};

function formatInput(input: Record<string, unknown>): string {
  if (!input) return '';
  const entries = Object.entries(input);
  if (entries.length === 0) return '(no args)';
  return entries
    .map(([k, v]) => {
      const val = Array.isArray(v) ? `[${(v as unknown[]).join(', ')}]` : String(v);
      return `${k}=${val}`;
    })
    .join('  ');
}

interface MessageBubbleProps {
  msg: Message;
}

const MessageBubble = ({ msg }: MessageBubbleProps): React.ReactElement => {
  const [thinkingOpen, setThinkingOpen] = useState(false);

  if (msg.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          style={{
            maxWidth: '70%',
            background: 'var(--bg-raised)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius-lg)',
            borderBottomRightRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            fontSize: '13px',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {msg.text}
        </div>
      </div>
    );
  }

  if (msg.role === 'system') {
    return (
      <div
        style={{
          padding: '16px 20px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderLeft: '3px solid var(--accent-cyan)',
          borderRadius: 'var(--radius-md)',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          maxWidth: '600px',
        }}
      >
        <MessageContent text={msg.text} />
      </div>
    );
  }

  if (msg.role === 'error') {
    return (
      <div
        style={{
          padding: '10px 14px',
          background: 'var(--accent-red-dim)',
          border: '1px solid var(--accent-red)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--accent-red)',
          fontSize: '12px',
        }}
      >
        {msg.text}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <div
        style={{
          width: '26px',
          height: '26px',
          flexShrink: 0,
          background: 'var(--bg-surface)',
          border: '1px solid var(--accent-cyan-dim)',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          marginTop: '2px',
          color: 'var(--accent-cyan)',
          fontWeight: 700,
          fontFamily: 'var(--font-ui)',
        }}
      >
        N
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {msg.thinking && (
          <button
            onClick={() => setThinkingOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              fontSize: '11px',
              fontFamily: 'var(--font-mono)',
              padding: '0 0 4px',
              marginBottom: '4px',
            }}
          >
            <span>◈</span>
            {thinkingOpen ? 'hide reasoning' : 'show reasoning'}
          </button>
        )}
        {thinkingOpen && msg.thinking && (
          <div
            style={{
              padding: '10px 12px',
              marginBottom: '8px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderLeft: '3px solid var(--text-muted)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '11px',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}
          >
            {msg.thinking}
          </div>
        )}
        {msg.toolCalls && msg.toolCalls.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            {msg.toolCalls.map((tc) => (
              <ToolCallCard
                key={tc.callId}
                name={tc.name}
                input={tc.input}
                result={tc.result}
                status={tc.status}
              />
            ))}
          </div>
        )}
        {msg.text && (
          <div style={{ fontSize: '13px', lineHeight: 1.7 }}>
            <MessageContent text={msg.text} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;